import { useQuery } from '@tanstack/react-query';
import { startOfMonth, endOfMonth, addHours } from 'date-fns';
import { supabase as _supabase } from '@/integrations/supabase/client';
import type { CalendarEvent } from '@/hooks/useCalendarEvents';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const supabase = _supabase as any;

export type AgendaItemSource = 'calendar_event' | 'task' | 'scheduled_call';

export interface AgendaItem extends CalendarEvent {
  _source: AgendaItemSource;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _raw: any;
}

const TASK_TYPE_COLORS: Record<string, string> = {
  whatsapp: '#25d366',
  task: '#6366f1',
  lembrete: '#f59e0b',
  default: '#8b5cf6',
};

const CALL_COLOR = '#10b981';

function mapTaskToAgendaItem(task: any): AgendaItem {
  // Flatten joined contact/deal into top-level fields for TaskDetailSheet
  const contact = task.contact;
  const deal = task.deal;
  const flatTask = {
    ...task,
    contact_first_name: contact?.first_name ?? null,
    contact_last_name: contact?.last_name ?? null,
    contact_phone: contact?.primary_phone ?? null,
    deal_name: deal?.name ?? null,
  };
  delete flatTask.contact;
  delete flatTask.deal;

  const dateStr = flatTask.due_date as string; // 'YYYY-MM-DD'
  const timeStr = flatTask.due_time as string | null; // 'HH:MM:SS' or null
  const allDay = !timeStr;

  let startAt: string;
  let endAt: string;

  if (timeStr) {
    const hhmm = timeStr.slice(0, 5); // 'HH:MM'
    startAt = `${dateStr}T${hhmm}:00`;
    const startDate = new Date(startAt);
    endAt = addHours(startDate, 1).toISOString();
  } else {
    startAt = `${dateStr}T00:00:00`;
    endAt = `${dateStr}T00:00:00`;
  }

  const metaType = (flatTask.metadata as any)?.type as string | undefined;
  const color = TASK_TYPE_COLORS[metaType ?? ''] ?? TASK_TYPE_COLORS.default;

  return {
    id: `task_${flatTask.id}`,
    title: flatTask.title,
    description: flatTask.description ?? null,
    start_at: startAt,
    end_at: endAt,
    all_day: allDay,
    type: 'other',
    status: flatTask.status === 'completed' ? 'completed' : 'scheduled',
    color,
    created_by: flatTask.created_by ?? null,
    attendee_ids: [],
    contact_id: flatTask.contact_id ?? null,
    deal_id: flatTask.deal_id ?? null,
    daily_room_url: null,
    daily_room_name: null,
    created_at: flatTask.created_at,
    updated_at: flatTask.updated_at,
    _source: 'task',
    _raw: flatTask,
  };
}

function mapCallToAgendaItem(call: any): AgendaItem {
  const startAt = call.scheduled_for as string;
  const startDate = new Date(startAt);

  // Detect all-day:
  //  1. metadata.all_day flag is the source of truth
  //  2. fallback: stored as YYYY-MM-DDT12:00:00Z (project convention for "no time")
  //  3. fallback: local time is exactly 00:00
  const isAllDay =
    !!(call.metadata as any)?.all_day ||
    (startDate.getUTCHours() === 12 &&
      startDate.getUTCMinutes() === 0 &&
      startDate.getUTCSeconds() === 0) ||
    (startDate.getHours() === 0 && startDate.getMinutes() === 0);

  // For all-day, anchor to local date (avoid UTC slice that can shift the day)
  const yyyy = startDate.getFullYear();
  const mm = String(startDate.getMonth() + 1).padStart(2, '0');
  const dd = String(startDate.getDate()).padStart(2, '0');
  const localDateOnly = `${yyyy}-${mm}-${dd}`;

  const effectiveStart = isAllDay ? `${localDateOnly}T00:00:00` : startAt;
  const effectiveEnd = isAllDay ? `${localDateOnly}T00:00:00` : addHours(startDate, 1).toISOString();

  return {
    id: `call_${call.id}`,
    title: call.title || 'Ligação agendada',
    description: null,
    start_at: effectiveStart,
    end_at: effectiveEnd,
    all_day: isAllDay,
    type: 'other',
    status: call.status ?? 'scheduled',
    color: CALL_COLOR,
    created_by: call.created_by ?? null,
    attendee_ids: [],
    contact_id: call.contact_id ?? null,
    deal_id: call.deal_id ?? null,
    daily_room_url: null,
    daily_room_name: null,
    created_at: call.created_at ?? startAt,
    updated_at: call.updated_at ?? startAt,
    _source: 'scheduled_call',
    _raw: call,
  };
}

export function useAgendaEvents(year: number, month: number, filterUserId?: string | null) {
  const isValidDate = Number.isFinite(year) && Number.isFinite(month) && month >= 1 && month <= 12;
  const monthStart = isValidDate ? startOfMonth(new Date(year, month - 1)).toISOString() : null;
  const monthEnd = isValidDate ? endOfMonth(new Date(year, month - 1)).toISOString() : null;
  const monthStartDate = isValidDate ? `${year}-${String(month).padStart(2, '0')}-01` : null;
  const monthEndDate = isValidDate
    ? new Date(year, month, 0).toISOString().slice(0, 10)
    : null;

  return useQuery({
    queryKey: ['agenda-events', year, month, filterUserId ?? 'self'],
    enabled: isValidDate && !!filterUserId,
    queryFn: async (): Promise<AgendaItem[]> => {
      const userId = filterUserId!;
      console.log('[AgendaEvents] queryFn fired', { year, month, userId, monthStart, monthEnd, monthStartDate, monthEndDate });

      // 1. calendar_events (existing logic)
      let ceQuery = supabase
        .from('calendar_events')
        .select('*')
        .gte('start_at', monthStart)
        .lte('start_at', monthEnd)
        .order('start_at', { ascending: true });

      ceQuery = ceQuery.or(`created_by.eq.${userId},attendee_ids.cs.{${userId}}`);

      const { data: ceData, error: ceError } = await ceQuery;
      console.log('[AgendaEvents] calendar_events result', { count: ceData?.length, error: ceError });
      if (ceError) throw ceError;

      const calendarItems: AgendaItem[] = ((ceData ?? []) as CalendarEvent[]).map((ev) => ({
        ...ev,
        _source: 'calendar_event' as AgendaItemSource,
        _raw: ev,
      }));

      // 2. tasks with due_date in this month assigned to user
      const { data: taskData, error: taskError } = await supabase
        .from('tasks')
        .select('id, title, description, status, priority, assigned_to, due_date, due_time, metadata, created_by, created_at, updated_at, contact_id, deal_id, contact:contacts(first_name, last_name, primary_phone), deal:deals(name)')
        .not('due_date', 'is', null)
        .eq('assigned_to', userId)
        
        .gte('due_date', monthStartDate)
        .lte('due_date', monthEndDate)
        .order('due_date', { ascending: true })
        .limit(500);

      if (taskError) throw taskError;
      console.log('[AgendaEvents] tasks result', { count: taskData?.length, error: taskError });
      const taskItems: AgendaItem[] = (taskData ?? []).map(mapTaskToAgendaItem);

      // 3. scheduled calls in this month for this user
      // Only fetch voice calls — video meetings are already shown via calendar_events
      const { data: callData, error: callError } = await supabase
        .from('calls')
        .select('id, title, scheduled_for, channel, status, to_e164, call_purpose, call_agenda, notes, lead_id, contact_id, deal_id, handled_by_app_user_id, created_by, created_at, updated_at, metadata, scheduled_by, original_scheduled_for, rescheduled_count, direction')
        .in('status', ['scheduled', 'canceled', 'completed'])
        .neq('channel', 'video')
        .eq('handled_by_app_user_id', userId)
        .gte('scheduled_for', monthStart)
        .lte('scheduled_for', monthEnd)
        .order('scheduled_for', { ascending: true })
        .limit(500);

      if (callError) throw callError;
      console.log('[AgendaEvents] calls result', { count: callData?.length, error: callError });
      const callItems: AgendaItem[] = (callData ?? []).map(mapCallToAgendaItem);

      // Deduplicate: remove calls that already appear as calendar_events (same title + start time)
      const calendarKeys = new Set(
        calendarItems.map(
          (item) =>
            `${new Date(item.start_at).toISOString()}_${(item.title ?? '').toLowerCase().trim()}`
        )
      );

      const filteredCallItems = callItems.filter((call) => {
        const callKey = `${new Date(call.start_at).toISOString()}_${(call.title ?? '').toLowerCase().trim()}`;
        return !calendarKeys.has(callKey);
      });

      console.log('[AgendaEvents] dedup', { callsBefore: callItems.length, callsAfter: filteredCallItems.length });

      return [...calendarItems, ...taskItems, ...filteredCallItems];
    },
    staleTime: 2 * 60 * 1000,
  });
}
